import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { leaveService, leaveTypeService } from '../../services/api';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';
import { calculateBusinessDays } from '../../utils/dateUtils';
import './LeaveForm.css';

export const LeaveForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    leaveTypeId: '',
    startDate: '',
    endDate: '',
    reason: '',
  });

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [businessDays, setBusinessDays] = useState(0);

  useEffect(() => {
    fetchLeaveTypes();
    fetchBalances();
  }, []);

  useEffect(() => {
    calculateDays();
  }, [formData.startDate, formData.endDate]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await leaveTypeService.getAll({ limit: 1000 });
      const types = response.data || [];
      setLeaveTypes(types);
      if (types.length > 0) {
        setFormData((prev) => ({
          ...prev,
          leaveTypeId: types[0].id,
        }));
      }
    } catch (err) {
      console.error('Failed to fetch leave types:', err);
    }
  };

  const fetchBalances = async () => {
    try {
      const response = await leaveService.getBalance(user?.id);
      const balancesMap = {};
      (response.data || []).forEach((balance) => {
        balancesMap[balance.leaveTypeId] = balance;
      });
      setBalances(balancesMap);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    }
  };

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) {
      setBusinessDays(0);
      return;
    }

    const start = parseISO(formData.startDate);
    const end = parseISO(formData.endDate);

    if (isBefore(end, start)) {
      setBusinessDays(0);
      return;
    }

    const days = calculateBusinessDays(start, end);
    setBusinessDays(days);
  };

  const validate = () => {
    const errors = [];

    if (!formData.leaveTypeId) {
      errors.push('Leave type is required');
    }

    if (!formData.startDate) {
      errors.push('Start date is required');
    }

    if (!formData.endDate) {
      errors.push('End date is required');
    }

    if (formData.startDate && formData.endDate) {
      const start = parseISO(formData.startDate);
      const end = parseISO(formData.endDate);

      if (isAfter(start, end)) {
        errors.push('End date must be after start date');
      }

      if (isBefore(start, startOfDay(new Date()))) {
        errors.push('Start date cannot be in the past');
      }
    }

    if (businessDays > 0) {
      const balance = balances[formData.leaveTypeId];
      const remaining = balance ? balance.remainingDays : 0;

      if (businessDays > remaining) {
        errors.push(
          `Insufficient balance. You have ${remaining} days available.`
        );
      }
    }

    if (!formData.reason.trim()) {
      errors.push('Reason is required');
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    if (errors.length > 0) {
      setError(errors.join('\n'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await leaveService.create({
        leaveTypeId: formData.leaveTypeId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        reason: formData.reason,
      });

      navigate('/leave');
    } catch (err) {
      setError('Failed to submit leave request');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRemaining = () => {
    const balance = balances[formData.leaveTypeId];
    if (!balance) return 0;
    return balance.remainingDays;
  };

  const remaining = getRemaining();

  return (
    <div className="leave-form-container">
      <div className="form-header">
        <h1>Request Leave</h1>
      </div>

      {error && (
        <div className="alert alert-error">
          {error.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="leave-form">
        <div className="form-group">
          <label htmlFor="leaveTypeId">Leave Type *</label>
          <select
            id="leaveTypeId"
            name="leaveTypeId"
            value={formData.leaveTypeId}
            onChange={handleInputChange}
            required
          >
            <option value="">Select leave type</option>
            {leaveTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {formData.leaveTypeId && (
          <div className="balance-info">
            <div className="balance-item">
              <span className="label">Available Balance:</span>
              <span className="value">{remaining} days</span>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startDate">Start Date *</label>
            <input
              id="startDate"
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              required
              min={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">End Date *</label>
            <input
              id="endDate"
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              required
              min={formData.startDate || format(new Date(), 'yyyy-MM-dd')}
            />
          </div>
        </div>

        {businessDays > 0 && (
          <div className="days-info">
            <div className="days-item">
              <span className="label">Business Days:</span>
              <span className={`value ${businessDays > remaining ? 'warning' : ''}`}>
                {businessDays}
              </span>
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="reason">Reason *</label>
          <textarea
            id="reason"
            name="reason"
            value={formData.reason}
            onChange={handleInputChange}
            placeholder="Enter the reason for your leave request"
            rows={5}
            required
          ></textarea>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/leave')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeaveForm;
